import { useEffect, useRef, useState } from 'react';

// T084 — Generic kebab (⋯) menu. Used to consolidate the less-common
// per-row actions on rec rows so the primary row UI stays readable.
//
// Items shape:
//   { label, onClick, disabled?, kbd?, danger?, divider? }
//
// `divider: true` renders a horizontal line instead of a menu item.

export default function KebabMenu({ items, align = 'right', label = 'More actions' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside-click / Escape. Escape returns focus to the ⋯ trigger.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // T125 — roving focus across the menu items. Opening focuses the first item;
  // ArrowUp/Down wrap, Home/End jump to the ends.
  const menuItems = () => [
    ...(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') ?? []),
  ];

  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onMenuKey = (e) => {
    const els = menuItems();
    if (els.length === 0) return;
    const i = els.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(i + 1) % els.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); els[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); els[els.length - 1]?.focus(); }
  };

  return (
    <span ref={wrapRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        className={
          'rounded border border-slate-700 px-2 py-1 text-xs leading-none transition ' +
          (open ? 'border-brand-600 bg-brand-600/10 text-brand-400' : 'text-slate-300 hover:border-brand-600 hover:text-brand-400')
        }
      >
        ⋯
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          onKeyDown={onMenuKey}
          className={
            'absolute z-20 mt-1 min-w-[180px] rounded-lg border border-slate-700 bg-slate-900/95 py-1 shadow-xl backdrop-blur ' +
            (align === 'right' ? 'right-0' : 'left-0')
          }
        >
          {items.map((item, i) => {
            if (item.divider) {
              return <div key={`d${i}`} className="my-1 border-t border-slate-800" />;
            }
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  item.onClick?.();
                  setOpen(false);
                }}
                className={
                  'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs disabled:opacity-50 disabled:cursor-not-allowed ' +
                  (item.danger
                    ? 'text-red-300 hover:bg-red-900/30 hover:text-red-200'
                    : 'text-slate-200 hover:bg-slate-800/70')
                }
              >
                <span>{item.label}</span>
                {item.kbd && (
                  <kbd className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">
                    {item.kbd}
                  </kbd>
                )}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
